import { useEffect, useRef } from 'react';

import useIsomorphicLayoutEffect from 'src/hooks/useIsomorphicLayoutEffect';

/**
 * Custom hook that handles timeouts in React components using the [setTimeout API](https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/setTimeout).
 *
 * @param callback - The function to be executed when the timeout elapses.
 * @param delay - The duration (in milliseconds) for the timeout. Set to null to clear the timeout.
 */
const useTimeout = (callback: () => void, delay: number | null): void => {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  useIsomorphicLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the timeout.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return;
    }

    const id = setTimeout(() => {
      savedCallback.current();
    }, delay);

    return () => {
      clearTimeout(id);
    };
  }, [delay]);
};

export default useTimeout;
